import { GreaterAreaDropdown } from '@/components/dropdowns/GreaterAreaDropdown';
import { LocationPicker } from '@/components/shared';
import { cn } from '@/lib/utils';
import {
  Button,
  FormFieldGroup,
  ImageUploadDropzone,
  Input,
} from '@startupquickstart/frontend';
import type { ChallengeEditorSectionProps } from '../types';
import { SectionCard } from './SectionCard';

interface LocationSectionProps extends ChallengeEditorSectionProps {
  googleMapsApiKey?: string;
}

/** Six-digit hex for the native color swatch input. */
const SWATCH_HEX_REGEX = /^#[0-9a-fA-F]{6}$/;
const SWATCH_FALLBACK = '#cccccc';

/** Parse a coordinate input into a number, or `null` when empty/invalid. */
function parseCoordinate(text: string): number | null {
  if (!text.trim()) return null;
  const parsed = parseFloat(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Zone + map location. The editor stores coordinates as numbers; the
 * conversion to the string-based `LocationPicker`/inputs happens here at the
 * component boundary. Event context adds the map-icon overrides (DT-165).
 */
export function LocationSection({
  values,
  errors,
  updateField,
  context,
  googleMapsApiKey,
}: LocationSectionProps) {
  const latitudeText = values.latitude?.toString() ?? '';
  const longitudeText = values.longitude?.toString() ?? '';

  return (
    <SectionCard id="location" title="Location" description="Optional location information">
      <FormFieldGroup name="zone" label="Zone">
        <Input
          id="zone"
          value={values.zone}
          onChange={(e) => updateField('zone', e.target.value)}
          placeholder="Enter zone name"
        />
      </FormFieldGroup>

      {/* Greater area (city/region) is a library-challenge concept only. */}
      {context === 'library' && (
        <FormFieldGroup
          name="greaterAreaId"
          label="Greater Area"
          description="The city or region this challenge belongs to"
          error={errors.greaterAreaId}
        >
          <GreaterAreaDropdown
            value={values.greaterAreaId ?? ''}
            onChange={(v) => updateField('greaterAreaId', v || null)}
          />
        </FormFieldGroup>
      )}

      <LocationPicker
        latitude={latitudeText}
        longitude={longitudeText}
        onLocationChange={(lat, lng) => {
          updateField('latitude', parseCoordinate(lat));
          updateField('longitude', parseCoordinate(lng));
        }}
        googleMapsApiKey={googleMapsApiKey}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <FormFieldGroup name="latitude" label="Latitude" error={errors.latitude}>
          <Input
            id="latitude"
            type="number"
            step="any"
            value={latitudeText}
            onChange={(e) => updateField('latitude', parseCoordinate(e.target.value))}
            placeholder="e.g., 40.7128"
          />
        </FormFieldGroup>

        <FormFieldGroup name="longitude" label="Longitude" error={errors.longitude}>
          <Input
            id="longitude"
            type="number"
            step="any"
            value={longitudeText}
            onChange={(e) => updateField('longitude', parseCoordinate(e.target.value))}
            placeholder="e.g., -74.0060"
          />
        </FormFieldGroup>
      </div>

      {context === 'event' && (
        <>
          {/* Map icon color override (DT-165) — empty falls back to the
              type-based default. Only applied to the "available" pin state. */}
          <FormFieldGroup
            name="mapIconColor"
            label="Map Icon Color"
            description="Hex color override for the map pin — leave blank for the type default"
            error={errors.mapIconColor}
          >
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Map icon color swatch"
                value={
                  SWATCH_HEX_REGEX.test(values.mapIconColor)
                    ? values.mapIconColor
                    : SWATCH_FALLBACK
                }
                onChange={(e) => updateField('mapIconColor', e.target.value)}
                className="h-10 w-12 cursor-pointer rounded border bg-background"
              />
              <Input
                id="mapIconColor"
                value={values.mapIconColor}
                onChange={(e) => updateField('mapIconColor', e.target.value)}
                placeholder="#1F2937"
                className={cn('flex-1 font-mono', errors.mapIconColor && 'border-destructive')}
              />
              {values.mapIconColor.trim() && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => updateField('mapIconColor', '')}
                  aria-label="Reset map icon color to default"
                  title="Reset to default"
                >
                  Reset
                </Button>
              )}
            </div>
          </FormFieldGroup>

          {/* Custom icon image (DT-165 phase B) — square 1:1 image rendered
              inside the map pin instead of the default type icon. */}
          <ImageUploadDropzone
            label="Custom Map Icon"
            targetAspectRatio={1}
            value={values.mapIconImageId || undefined}
            onChange={(mediaId) => updateField('mapIconImageId', mediaId || '')}
          />
        </>
      )}
    </SectionCard>
  );
}
